import { useEffect } from "react";
import { create } from "zustand";
import { formatBytes, formatDuration } from "@hybrid-iptv/recorder";

/**
 * Scheduled DVR (Section 20), page side. The Android app records on its own —
 * alarms, a foreground recording service and a guide check every 2 hours, all in
 * native code (native/android/.../dvr) — and this is how the screens book,
 * list and configure it. Outside the APK there is no DVR: dvrAvailable() is
 * false and nothing here is called.
 */

export type DvrStatus = "scheduled" | "recording" | "done" | "partial" | "failed" | "cancelled" | "skipped" | "planned";

export interface DvrRecording {
  id: string;
  ruleId?: string;
  title: string;
  subtitle?: string;
  /** "s2e5" when known. */
  episodeKey?: string;
  channelId?: string;
  channelName?: string;
  startMs: number;
  endMs: number;
  padBeforeMs: number;
  padAfterMs: number;
  status: DvrStatus;
  volumeId?: string;
  bytes: number;
  error?: string;
  gaps: number;
  finishedAt: number;
  /** file:// URI of the recording, once there is one. */
  fileUri?: string;
}

export interface DvrRule {
  id: string;
  title: string;
  channelId?: string;
  channelName?: string;
  newOnly: boolean;
  keep: number;
}

export interface DvrVolume {
  id: string;
  label: string;
  removable: boolean;
  freeBytes: number;
  totalBytes: number;
  reserveBytes: number;
}

export interface DvrState {
  recordings: DvrRecording[];
  rules: DvrRule[];
  volumes: DvrVolume[];
  volumeId: string;
  padBeforeMs: number;
  padAfterMs: number;
  maxConnections: number;
  guideConfigured: boolean;
  guideChecking: boolean;
  lastGuideCheckAt: number;
  lastGuideCheckError?: string;
  lastGuideCheckBooked: number;
  /** The latest programme end in the provider's guide at the last check; 0 before one. */
  guideHorizonMs: number;
  exactAlarms: boolean;
  assumedBps: number;
}

export interface DvrAdmission {
  ok: boolean;
  reason?: "not_enough_space" | "no_free_connection" | "already_ended";
  neededBytes: number;
  availableBytes: number;
  overlapping: number;
  maxConnections: number;
  volumeId: string;
  volumeLabel: string;
  id?: string;
}

export interface DvrProgrammeRequest {
  title: string;
  subtitle?: string;
  channelId: string;
  channelName: string;
  /** The stream URL with the viewer's credentials (resolveStreamUrl on the device). */
  url: string;
  startMs: number;
  endMs: number;
  ruleId?: string;
  /** Tells repeats apart when the guide has no episode numbers or titles. */
  description?: string;
}

export interface DvrChannelEntry {
  feedChannelId: string;
  /** The guide listing's own name, so the DVR can pick the best-fitting channel. */
  feedName?: string;
  channelId: string;
  name: string;
  url: string;
  /** Minutes the guide's times are moved by for this channel (Phase 3 step 4); none when 0. */
  shiftMinutes?: number;
}

interface CapacitorLike {
  isNativePlatform?: () => boolean;
  PluginHeaders?: Array<{ name: string }>;
  nativePromise?: (plugin: string, method: string, options: Record<string, unknown>) => Promise<unknown>;
  addListener?: (plugin: string, event: string, callback: (data: unknown) => void) => { remove: () => unknown };
}

const PLUGIN = "Dvr";

function capacitor(): CapacitorLike | null {
  const cap = (globalThis as { Capacitor?: CapacitorLike }).Capacitor;
  if (cap?.isNativePlatform?.() !== true) return null;
  if (typeof cap.nativePromise !== "function" || typeof cap.addListener !== "function") return null;
  if (!cap.PluginHeaders?.some((header) => header.name === PLUGIN)) return null;
  return cap;
}

/** True inside the Android app, where recordings can be scheduled. */
export function dvrAvailable(): boolean {
  return capacitor() !== null;
}

function call<T>(method: string, options: Record<string, unknown> = {}): Promise<T> {
  const cap = capacitor();
  if (!cap) return Promise.reject(new Error("dvr_unavailable"));
  return cap.nativePromise!(PLUGIN, method, options) as Promise<T>;
}

export const dvr = {
  getState: () => call<DvrState>("getState"),
  setSettings: (settings: { volumeId?: string; padBeforeMs?: number; padAfterMs?: number }) => call<void>("setSettings", settings),
  /** A source the DVR cannot read (no guide or no Xtream login) is sent with every field absent and no channels: it forgets the previous one. */
  syncSource: (source: { epgUrl?: string; xtreamBaseUrl?: string; xtreamUsername?: string; xtreamPassword?: string; channels: DvrChannelEntry[] }) =>
    call<void>("syncSource", source as unknown as Record<string, unknown>),
  preview: (request: DvrProgrammeRequest) => call<DvrAdmission>("preview", request as unknown as Record<string, unknown>),
  schedule: (request: DvrProgrammeRequest) => call<DvrAdmission>("schedule", request as unknown as Record<string, unknown>),
  addRule: (rule: { title: string; channelId?: string; channelName?: string; preferChannelId?: string; newOnly: boolean; keep: number }) =>
    call<{ id: string }>("addRule", rule),
  removeRule: (id: string) => call<void>("removeRule", { id }),
  checkGuideNow: () => call<void>("checkGuideNow"),
  cancel: (id: string) => call<void>("cancel", { id }),
  remove: (id: string) => call<void>("remove", { id }),
  openAlarmSettings: () => call<void>("openAlarmSettings"),
  /** The provider's stream limit, streams open on the account (all devices) and recordings running here. */
  connections: () => call<{ maxConnections: number; activeConnections: number; recordingNow: number }>("connections"),
};

interface DvrStore {
  state: DvrState | null;
  refresh: () => Promise<void>;
}

export const useDvrStore = create<DvrStore>((set) => ({
  state: null,
  refresh: async () => {
    if (!dvrAvailable()) return;
    try {
      set({ state: await dvr.getState() });
    } catch {
      // Keep the last state; the next change event tries again.
    }
  },
}));

let listening = false;

/** The DVR's state, kept current by the native "change" events. */
export function useDvr(): { available: boolean; state: DvrState | null; refresh: () => Promise<void> } {
  const state = useDvrStore((s) => s.state);
  const refresh = useDvrStore((s) => s.refresh);
  const available = dvrAvailable();
  useEffect(() => {
    if (!available) return;
    void refresh();
    if (listening) return;
    listening = true;
    capacitor()?.addListener?.(PLUGIN, "change", () => void useDvrStore.getState().refresh());
  }, [available, refresh]);
  return { available, state, refresh };
}

// --- Wording -----------------------------------------------------------------

export { formatBytes };

/** What is still usable on a volume after the safety reserve, as recording time. */
export function volumeHoursLabel(volume: DvrVolume, bps: number): string {
  const usable = Math.max(0, volume.freeBytes - volume.reserveBytes);
  return formatDuration(Math.floor(usable / (bps / 8)));
}

export function admissionMessage(admission: DvrAdmission): string {
  switch (admission.reason) {
    case "not_enough_space":
      return (
        `Not enough space on ${admission.volumeLabel}. This recording needs about ${formatBytes(admission.neededBytes)}, ` +
        `and only ${formatBytes(Math.max(0, admission.availableBytes))} is left after other recordings and a safety reserve.`
      );
    case "no_free_connection":
      return (
        `Your provider allows ${admission.maxConnections} stream${admission.maxConnections === 1 ? "" : "s"} at a time, ` +
        `and ${admission.overlapping} recording${admission.overlapping === 1 ? " already overlaps" : "s already overlap"} this one.`
      );
    case "already_ended":
      return "This programme has already ended.";
    default:
      return `Needs about ${formatBytes(admission.neededBytes)} · ${formatBytes(Math.max(0, admission.availableBytes))} free on ${admission.volumeLabel}`;
  }
}

const ERROR_TEXT: Record<string, string> = {
  missed: "The device was off or unplugged at the time",
  not_enough_space: "Not enough space when it was time to record",
  no_free_connection: "No free stream on your provider for it",
  storage_full: "Stopped early: storage ran low",
  stopped: "Stopped early",
  write_failed: "Couldn't write to storage",
  no_data: "The channel sent nothing",
  network: "Couldn't reach the channel",
  removed_keep_latest: "Removed to keep the latest episodes",
  deleted: "Deleted",
};

export function recordingErrorText(error: string | undefined): string {
  if (!error) return "";
  if (error.startsWith("http_")) return `The channel refused the connection (${error.slice(5)})`;
  return ERROR_TEXT[error] ?? "Something went wrong";
}
